import type { ModelId, ModelMixSegment, SavingsTask } from '@/domains/pages/model-savings/types';
import { formatChartCurrencyMetricValue, formatNumber, FORMAT_PRESETS } from '@/utils/format.util';

export function sortTasksBySaving(tasks: SavingsTask[]): SavingsTask[] {
  return [...tasks].sort((a, b) => b.savingAnnual - a.savingAnnual);
}

export function sortSegmentsBySaving<T extends { savingAnnual: number }>(segments: T[]): T[] {
  return [...segments].sort((a, b) => b.savingAnnual - a.savingAnnual);
}

export function getTopTasks(tasks: SavingsTask[], count = 5): SavingsTask[] {
  return sortTasksBySaving(tasks).slice(0, count);
}

export function sumTasksSavingAnnual(tasks: Pick<SavingsTask, 'savingAnnual'>[]): number {
  return tasks.reduce((sum, task) => sum + task.savingAnnual, 0);
}

/** Savings from a subset of tasks, as a share of total annualized spend. */
export function computeTasksSavingShareOfSpendPercent(
  tasks: Pick<SavingsTask, 'savingAnnual'>[],
  totalSpendAnnual: number,
): number {
  if (totalSpendAnnual <= 0) return 0;
  return Math.round((sumTasksSavingAnnual(tasks) / totalSpendAnnual) * 100);
}

export function computeTopTasksSavingsSharePercent(
  tasks: Pick<SavingsTask, 'savingAnnual'>[],
  totalSavingAnnual: number,
): number {
  if (totalSavingAnnual <= 0) return 0;
  return Math.round((sumTasksSavingAnnual(tasks) / totalSavingAnnual) * 100);
}

/** The tier the mix leans on most, or null when the mix is empty. */
export function dominantMixSegment(mix: ModelMixSegment[]): ModelMixSegment | null {
  const top = mix.reduce<ModelMixSegment | null>(
    (currentTop, segment) =>
      currentTop == null || segment.percent > currentTop.percent ? segment : currentTop,
    null,
  );

  return top != null && top.percent > 0 ? top : null;
}

export function mixPercent(mix: ModelMixSegment[], modelId: ModelId): number {
  return mix.find((segment) => segment.modelId === modelId)?.percent ?? 0;
}

export function computeLighterCouldUsePercent(actualMix: ModelMixSegment[], optimalMix: ModelMixSegment[]): number {
  const actualFrontier = mixPercent(actualMix, 'opus');
  const optimalFrontier = mixPercent(optimalMix, 'opus');
  const actualMedium = mixPercent(actualMix, 'sonnet');
  const optimalMedium = mixPercent(optimalMix, 'sonnet');
  const actualFlagship = mixPercent(actualMix, 'flagship');
  const optimalFlagship = mixPercent(optimalMix, 'flagship');

  return Math.round(
    Math.max(0, actualFrontier - optimalFrontier) +
      Math.max(0, actualMedium - optimalMedium) +
      Math.max(0, actualFlagship - optimalFlagship),
  );
}

export function paginateTasks(tasks: SavingsTask[], pageIndex: number, pageSize: number): SavingsTask[] {
  const sorted = sortTasksBySaving(tasks);
  const start = pageIndex * pageSize;
  return sorted.slice(start, start + pageSize);
}

export function calcTotalPages(totalItems: number, pageSize: number): number {
  if (pageSize <= 0) return 0;
  return Math.max(1, Math.ceil(totalItems / pageSize));
}

export function getTaskNeighborIndex(tasks: SavingsTask[], taskId: string): number {
  const sorted = sortTasksBySaving(tasks);
  return sorted.findIndex((task) => task.id === taskId);
}

export function mixChangePoints(actual: ModelMixSegment[], optimal: ModelMixSegment[], modelId: string): number {
  const actualPercent = actual.find((segment) => segment.modelId === modelId)?.percent ?? 0;
  const optimalPercent = optimal.find((segment) => segment.modelId === modelId)?.percent ?? 0;
  return optimalPercent - actualPercent;
}

export function formatAnnualSavings(value: number): string {
  return formatChartCurrencyMetricValue(value);
}

export function formatAnnualSpend(value: number): string {
  return formatChartCurrencyMetricValue(value);
}

export function formatCompactSavingsPill(value: number): string {
  return formatChartCurrencyMetricValue(Math.abs(value));
}

/** Compact count (e.g. 1200 -> 1.2K, 3400000 -> 3.4M). */
export function formatCount(count: number | null | undefined): string {
  return formatNumber(count, FORMAT_PRESETS.axis);
}
